import { useState } from "react";
import {
  ChevronDownIcon,
  FileTextIcon,
  PaperclipIcon,
  PlusIcon,
  XIcon,
} from "lucide-react";

import { CompanyAttachmentViewerDialog } from "./CompanyAttachmentViewerDialog";
import { CreateCompanyFileDialog } from "@/components/companies/files/CreateCompanyFileDialog";
import { Column, ConfirmationDialog, Row } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";

import { useUpdateSetCompanyIntake } from "@/hooks/updateSets/useUpdateSetCompanyIntake";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { CompanyFile, CompanyIntake } from "@/types";

export const Attachments = ({
  companyIntake,
}: {
  companyIntake: CompanyIntake;
}) => {
  const { mutate: updateCompanyIntake } = useUpdateSetCompanyIntake();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState<{
    id: number;
    mimetype: string;
  } | null>(null);
  const [deleteAttachmentId, setDeleteAttachmentId] = useState<number | null>(
    null,
  );

  const attachments = companyIntake.attachments ?? [];

  const handleDeleteAttachment = (id: number) => {
    updateCompanyIntake({
      updateCompanyIntakeRequest: {
        attachments: attachments.filter((a) => a.id !== id),
      },
    });
    setDeleteAttachmentId(null);
  };

  const handleNewAttachment = (newCompanyFile?: CompanyFile) => {
    if (!newCompanyFile) return;
    updateCompanyIntake({
      updateCompanyIntakeRequest: {
        attachments: [
          ...attachments,
          {
            id: newCompanyFile.id,
            name: newCompanyFile.name,
            url: newCompanyFile.url,
            mimetype: newCompanyFile.mimeType,
          },
        ],
      },
    });
    setDialogOpen(false);
  };

  return (
    <>
      <Collapsible className="group/collapsible mt-2 border-t border-slate-200">
        <CollapsibleTrigger asChild>
          <Button
            className="w-full justify-between px-2 hover:bg-slate-50"
            variant="ghost"
          >
            <Row className="items-center gap-2">
              <PaperclipIcon className="h-4 w-4 text-slate-500" />
              <Label className="text-base font-normal text-slate-600">
                Attachments ({attachments.length})
              </Label>
            </Row>
            <ChevronDownIcon className="h-4 w-4 rotate-180 text-slate-400 transition-transform group-data-[state=open]/collapsible:rotate-0" />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Column className="gap-1 px-2 pb-2">
            {attachments.map((attachment) => (
              <Row key={attachment.id} className="group/attachment relative">
                <Button
                  className="w-full justify-start px-2 hover:bg-slate-50 group-hover/attachment:pr-8"
                  onClick={() => {
                    mixpanelUtils.trackButtonClick(
                      "View attachment",
                      MIXPANEL_SCREENS.COMPANY,
                    );
                    setSelectedAttachment({
                      id: attachment.id,
                      mimetype: attachment.mimetype,
                    });
                  }}
                  variant="ghost"
                >
                  <FileTextIcon className="h-4 w-4 shrink-0 text-slate-400" />
                  <Label
                    className="truncate text-sm text-slate-700"
                    title={attachment.name}
                  >
                    {attachment.name}
                  </Label>
                </Button>
                <Button
                  className="absolute right-1 top-1/2 hidden h-5 w-5 -translate-y-1/2 text-slate-400 hover:bg-transparent hover:text-slate-900 group-hover/attachment:flex"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteAttachmentId(attachment.id);
                  }}
                  size="icon"
                  variant="ghost"
                >
                  <XIcon className="h-3 w-3" />
                </Button>
              </Row>
            ))}
            <Button
              className="hover:border-primary hover:text-primary hover:bg-primary/5 mt-1 w-full border-dashed border-slate-300 text-sm text-slate-500"
              onClick={() => {
                mixpanelUtils.trackButtonClick(
                  "Add attachment",
                  MIXPANEL_SCREENS.COMPANY,
                );
                setDialogOpen(true);
              }}
              size="sm"
              type="button"
              variant="outline"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add Attachment
            </Button>
          </Column>
        </CollapsibleContent>
      </Collapsible>

      <CreateCompanyFileDialog
        onOpenChange={setDialogOpen}
        onSuccess={handleNewAttachment}
        open={dialogOpen}
      />

      <ConfirmationDialog
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        confirmationButtonVariant="destructive"
        description="This will remove the attachment from the company intake."
        onConfirm={() => {
          mixpanelUtils.trackButtonClick(
            "Delete attachment",
            MIXPANEL_SCREENS.COMPANY,
          );
          if (deleteAttachmentId != null) {
            handleDeleteAttachment(deleteAttachmentId);
          }
        }}
        onOpenChange={(open) => {
          if (!open) setDeleteAttachmentId(null);
        }}
        open={deleteAttachmentId != null}
        title="Delete attachment?"
      />

      <CompanyAttachmentViewerDialog
        companyFileId={selectedAttachment?.id}
        mimeType={selectedAttachment?.mimetype}
        onOpenChange={(open) => {
          if (!open) setSelectedAttachment(null);
        }}
        open={!!selectedAttachment}
      />
    </>
  );
};
